import type { DBSchema, StoreNames } from 'idb';
import type { RecordResult } from '../@types/records';
import type { SyncDetails } from '../redux/middleware/sync-transforms';

export const OdinDBStores = {
  ApiQueries: 'api-queries',
  RecordMutations: 'record-mutations',
} as const;
export type OdinDBStores = (typeof OdinDBStores)[keyof typeof OdinDBStores];

type TaskForStore<S extends StoreNames<OdinDBSchema>> = {
  storeName: S;
  indexName: keyof OdinDBSchema[S]['indexes'] & string;
};

export const CleanupStrategies = {
  DeleteExpired: 'delete-expired',
  DeleteByExactMatch: 'delete-by-exact-match',
} as const;
export type CleanupStrategies = (typeof CleanupStrategies)[keyof typeof CleanupStrategies];

/** Cleanup task for the odin database */
export type CleanupTask =
  | (TaskForStore<typeof OdinDBStores.ApiQueries> & {
      strategy: typeof CleanupStrategies.DeleteExpired; // Delete items where index value <= now
    })
  | (TaskForStore<typeof OdinDBStores.RecordMutations> & {
      strategy: typeof CleanupStrategies.DeleteByExactMatch; // Delete items where index value === matchValue
      matchValue: string; // e.g., 'synced'
    });

/* Definition for the Odin database schema. */
export interface OdinDBApiQueryEntry {
  key: string; // composite key: `${endpoint}|${argsHash}`
  data: unknown; // cached response data
  metadata: OdinDBApiQueryEntryMetadata;
}

export interface OdinDBApiQueryEntryMetadata {
  endpoint: string;
  argsHash: string; // stable hash of the query args
  updatedAt: number; // timestamp of last update
  expiresAt: number; // timestamp when this entry expires
}

/**
 * State for record mutation sync attempts.
 *
 * - 'pending': Awaiting sync.
 * - 'processing': Currently being synced; used to prevent duplicate sync attempts.
 * - 'synced': Successfully synced.
 * - 'error': Sync failed.
 * - 'conflict': Sync failed due to a 900 merge conflict
 */
export type OdinDBRecordMutationState = 'pending' | 'synced' | 'error' | 'processing' | 'conflict';

/**
 * Options for updating the mutation state of a record in the Odin database.
 *
 * - 'newState' - The target state to transition the record to.
 * - 'expectedState' - Optional. The expected current state of the record for optimistic concurrency control.
 * - 'error' - Optional. An error message associated with the state transition, if applicable.
 * - 'incrementRetryCount' - Optional. Whether to increment the retry counter for the operation.
 */
export interface UpdateRecordStateOptions {
  newState: OdinDBRecordMutationState;
  expectedState?: OdinDBRecordMutationState;
  error?: string;
  incrementRetryCount?: boolean;
}

/**
 * Operation type for record mutations.
 *
 * - 'create': New record to be created.
 * - 'update': Existing record to be updated.
 * - 'delete': Existing record to be deleted.
 */
export type OdinDBRecordMutationOperation = 'create' | 'update' | 'delete';

export interface OdinDBRecordMutationMetadata {
  createdAt?: number;
  updatedAt?: number;
  state: OdinDBRecordMutationState;
  operation: OdinDBRecordMutationOperation;
  error?: string;
  retryCount?: number; // number of retry attempts made
}

export interface OdinDBRecordMutation {
  id: number;
  record: RecordResult; // the changes to be applied
  baseRecord?: RecordResult; // the record state when the mutation was created
  metadata: OdinDBRecordMutationMetadata;
  syncDetails?: SyncDetails;
}

export interface OdinDBSchema extends DBSchema {
  [OdinDBStores.ApiQueries]: {
    key: string; // composite key: `${endpoint}|${argsHash}`
    value: OdinDBApiQueryEntry;
    indexes: { 'by-endpoint': string; 'by-expiresAt': number };
  };
  [OdinDBStores.RecordMutations]: {
    key: number;
    value: OdinDBRecordMutation;
    indexes: { 'by-formId': number; 'by-state': string; 'by-operation': string };
  };
}

export interface InitDBConfig {
  userId: string;
  schema: string;
  onCleanupComplete?: () => void;
}
